import {
    Button,
    Checkbox,
    Grid,
    InputAdornment,
    ListItemText,
    MenuItem,
    Paper,
    TextField,
    Typography
} from "@mui/material";
import {useTranslation} from "react-i18next";
import SectionHeader from "../../../../util/component/SectionHeader";
import '../../style/SalesMarketing.css';
import * as Yup from 'yup';
import {useNavigate, useParams} from "react-router-dom";
import * as React from "react";
import {useContext, useEffect, useState} from "react";
import {
    INTEGER_REG_EXP,
    MAIL_ADDRESS_REG_EXP,
    POI_STATUS_DISMISSED,
    POI_STATUS_DRAFT,
    POI_STATUS_READY,
    SIGNED_DECIMAL_REG_EXP
} from "../../component/Constants";
import {usePoiActions} from "../../service/usePoiActions";
import {useStatusValuesPoi} from "../../component/useStatusValuesPoi";
import {Controller, useFieldArray, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {CheckCircleRounded, DoDisturbAltRounded, LibraryAddRounded} from "@mui/icons-material";
import EmptyState from "../../../../util/component/EmptyState";
import PoiType from "./sections/PoiType";
import CustomModal from "../../component/CustomModal";
import {GlobalContext} from "../../../../util/state/GlobalContextProvider";
import {PoiService} from "./PoiInterfaces";
import {
    preselectSingleEntity,
    UserLegalEntitiesOrDealersInputGroup
} from "../../component/UserLegalEntitiesOrDealersInputGroup/UserLegalEntitiesOrDealersInputGroup";
import GoogleAutocompleteWrapper from "../../../../util/component/GoogleAutocompleteWrapper";
import IconButton from "@mui/material/IconButton";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { limitAfterDot } from "../../component/Utils";

interface PoiDetail {
    id: number,
    name: string,
    tenantId: string,
    dealerId: string,
    status: string,
    idPoiServiceList: number[],
    address: string,
    latitude: string,
    longitude: string,
    poiTemplateDetailDTOList: any[],
    city: string
}


const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const PoiCreateOrUpdatePage = () => {
    const {poiId} = useParams();
    const isUpdate = !!poiId;
    const {globalData} = useContext(GlobalContext);
    const {t} = useTranslation(['salesmarketing'], {keyPrefix: 'poi'});
    const navigate = useNavigate();
    const [poi, setPoi] = useState<PoiDetail>();
    const [poiServices, setPoiServices] = useState<PoiService[]>([]);
    const [autoFilled, setAutoFilled] = useState(false);
    const [hasStreetNumber,setHasStreetNumber] = useState(false);

    const {getPoiById, createPoi, updatePoi, getPoiServices} = usePoiActions();


    const {getStatusLabelPoi, getStatusColorPoi} = useStatusValuesPoi();

    const [openCreationModal, setOpenCreationModal] = useState(false);

    const handleCitySelection = (place: any): void => {
        let city: string = place.address_components.find((a: any) => a.types.includes('locality'))?.long_name;
        let streetName: string = place.address_components.find((a: any) => a.types.includes('route'))?.long_name;
        let streetNumber: string = place.address_components.find((a: any) => a.types.includes('street_number'))?.long_name;
        let completeAddress = streetName + (streetNumber ? `, ${streetNumber}` : '');
        setValue('city', city);
        setValue('address', completeAddress);
        if (streetNumber) {
            let lat = limitAfterDot(place.geometry.location.lat().toString(),12);
            let lon =limitAfterDot(place.geometry.location.lng().toString(),12);
            setValue('longitude', lon);
            setValue('latitude', lat);
            setHasStreetNumber(true);
        } else {
            setValue('latitude','');
            setValue('longitude','');
            setHasStreetNumber(false);
        }

        setAutoFilled(true);
    }

    const statuses = [
        {
            value: POI_STATUS_DRAFT,
            label: getStatusLabelPoi(POI_STATUS_DRAFT),
            color: getStatusColorPoi(POI_STATUS_DRAFT)
        },
        {
            value: POI_STATUS_READY,
            label: getStatusLabelPoi(POI_STATUS_READY),
            color: getStatusColorPoi(POI_STATUS_READY)
        },
        {
            value: POI_STATUS_DISMISSED,
            label: getStatusLabelPoi(POI_STATUS_DISMISSED),
            color: getStatusColorPoi(POI_STATUS_DISMISSED)
        },

    ];

    useEffect(() => {
        getPoiServices().then(res => {
            setPoiServices(res.result);
        });
    }, [])

    useEffect(() => {
        if (poiId && isUpdate) {
            // retrieve poi
            getPoiById(poiId).then(res => {
                setPoi(res);
            });
        }
    }, []);

    useEffect(() => {
        reset(retrieveInitialValues())
    }, [poi])

    const retrieveInitialValues = () => {
        if (isUpdate && poi) {
            return {
                id: poi.id ? poi.id : null,
                tenantId: poi.tenantId ? poi.tenantId : "",
                dealerId: poi.dealerId ? poi.dealerId : "",
                status: poi.status ? poi.status : "",
                idPoiServiceList: poi.idPoiServiceList ? poi.idPoiServiceList : [],
                address: poi.address ? poi.address : "",
                name: poi.name ? poi.name : "",
                longitude: poi.longitude ? poi.longitude : "",
                latitude: poi.latitude ? poi.latitude : "",
                poiTemplateDetailDTOList: poi.poiTemplateDetailDTOList ? poi.poiTemplateDetailDTOList : [],
                city: poi.city ? poi.city : ''
            };
        } else {
            return {
                tenantId: "",
                dealerId: "",
                status: "",
                idPoiServiceList: [],
                address: "",
                name: "",
                longitude: "",
                latitude: "",
                poiTemplateDetailDTOList: [],
                city: ''
            };
        }
    }

    Yup.addMethod(Yup.array, "unique", function (message, path) {
        return this.test("unique", message, function (list) {
            const mapper = (x: any) => x.type;
            // @ts-ignore
            const set = [...new Set(list!.map(mapper))];
            const isUnique = list!.length === set.length;
            if (isUnique) {
                return true;
            }

            const idx = list!.findIndex((l, i) => mapper(l) !== set[i]);
            return this.createError({
                path: `poiTemplateDetailDTOList[${idx}].type`,
                message: message,
            });
        });
    });

    const validationSchema = Yup.object({
        tenantId: Yup.string().nullable().test('testTenant', t('createForm.requiredCheck'), (value) => {
            if (globalData?.user?.legalEntities?.length === 1)
                return true;
            return !!value;
        }),
        dealerId: Yup.string().nullable().test('testDealer', t('createForm.requiredCheck'), (value) => {
            if (globalData?.user?.dealers?.length === 1)
                return true;
            return !!value;
        }),
        status: Yup.string().required(t('createForm.requiredCheck')),
        idPoiServiceList: Yup.array().min(1, (t('createForm.requiredCheck'))),
        address: Yup.string().required(t('createForm.requiredCheck')),
        city: Yup.string().required(t('createForm.requiredCheck')),
        name: Yup.string().required(t('createForm.requiredCheck')),
        latitude: Yup.string().matches(SIGNED_DECIMAL_REG_EXP, {
            excludeEmptyString: true,
            message: t('createForm.onlyDecimalNumbers')
        }).max(15, t('maxLength')).required(t('createForm.requiredCheck')),
        longitude: Yup.string().matches(SIGNED_DECIMAL_REG_EXP, {
            excludeEmptyString: true,
            message: t('createForm.onlyDecimalNumbers')
        }).max(15, t('maxLength')).required(t('createForm.requiredCheck')),
        poiTemplateDetailDTOList: Yup.array().of(Yup.object().shape({
            type: Yup.string().required(t('createForm.requiredCheck')),
            email: Yup.string().matches(MAIL_ADDRESS_REG_EXP, {
                excludeEmptyString: true,
                message: t('createForm.emailNotValid')
            }),
            numberOfConnectors: Yup.string().matches(INTEGER_REG_EXP, {
                excludeEmptyString: true,
                message: t('createForm.onlyNumbers')
            }),
            phoneNumber: Yup.string().matches(INTEGER_REG_EXP, {
                excludeEmptyString: true,
                message: t('createForm.onlyNumbers')
            }),
            responsiblePhoneNumber: Yup.string().matches(INTEGER_REG_EXP, {
                excludeEmptyString: true,
                message: t('createForm.onlyNumbers')
            }),
            assistanceTypeDetail: Yup.array(),
            mechanicalWorkshop:Yup.string(),
            openingHours: Yup.string(),
            dmsId: Yup.string().matches(INTEGER_REG_EXP, {
                excludeEmptyString: true,
                message: t('createForm.onlyNumbers')
            }),
        }))
            // @ts-ignore
            .unique(t('createForm.duplicatedType'))
    });

    const {
        handleSubmit,
        control,
        setValue,
        watch,
        reset,
        formState,
        setError,
        clearErrors,
        getValues,
        resetField
    } = useForm({
        defaultValues: retrieveInitialValues(),
        resolver: yupResolver(validationSchema)
    });

    const cleanAddress = (): void => {
        resetField('city');
        resetField('address');
        resetField('longitude');
        resetField('latitude');
        setAutoFilled(false);
    }

    const onSubmit = async (values: any) => {
        values.poiTemplateDetailDTOList.map((type: any) => {
            return delete type.id
        });
        if (globalData?.user?.legalEntities?.length === 1) {
            values = {...values, tenantId: globalData?.user?.legalEntities[0].name};
        }

        if (globalData?.user?.dealers?.length === 1) {
            values = {...values, dealerId: globalData?.user?.dealers[0].name}
        }

        if (checkList()) {
            let payload = {...values};
            if (isUpdate) {
                let data = await updatePoi(payload);
                if (data && data.status === 'OK') {
                    // show modal with confirm
                    setOpenCreationModal(true);
                } else {
                    // show snackbar with error
                }
            } else {
                let data = await createPoi(payload);
                if (data && data.status === 'OK') {
                    // show modal with confirm
                    setOpenCreationModal(true);
                } else {
                    // show snackbar with error
                }
            }
        }
    }

    function checkList() {
        let result = {isOk: true};
        // other checks
        return result.isOk;
    }

    const {fields, append, remove, update} = useFieldArray({
        control,
        name: "poiTemplateDetailDTOList"
    });

    const addSection = () => {
        append({type: ""});
    }

    useEffect(() => {
        preselectSingleEntity(globalData, setValue);
    }, [globalData, setValue]);

    return (
        <>
            <SectionHeader
                title={t(isUpdate ? 'updateHeader.title' : 'createHeader.title')}
                subtitle={t('createHeader.subTitle')}/>
            <Paper elevation={3} sx={{padding: "16px", margin: "18px"}}>
                <form onSubmit={handleSubmit(data => {
                    onSubmit({...data});
                }, errors => console.log(errors))} noValidate autoComplete={'off'}>
                    <Grid container rowSpacing={3} columnSpacing={3}>
                        <UserLegalEntitiesOrDealersInputGroup control={control} disabled={isUpdate}/>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"name"}
                                render={({field, fieldState}) => (
                                    <TextField
                                        fullWidth
                                        name="name"
                                        required
                                        label={t('name')}
                                        value={field.value}
                                        onChange={field.onChange}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"status"}
                                render={({field, fieldState}) => (
                                    <TextField
                                        fullWidth
                                        required
                                        name="status"
                                        label={t('status')}
                                        select
                                        value={field.value}
                                        onChange={field.onChange}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    >
                                        <MenuItem key={""} value={""}/>
                                        {statuses?.map((a) => (
                                            <MenuItem key={a.value} value={a.value}>
                                                {a.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}/>
                        </Grid>
                        {poiServices.length > 0 &&
                            <Grid item xs={3}>
                                <Controller
                                    control={control}
                                    name={"idPoiServiceList"}
                                    render={({field, fieldState}) => (
                                        <TextField
                                            fullWidth
                                            name="idPoiServiceList"
                                            label={t('service')}
                                            select
                                            required
                                            value={field.value}
                                            onChange={field.onChange}
                                            error={!!fieldState.error}
                                            helperText={fieldState.error?.message}
                                            SelectProps={{
                                                multiple: true,
                                                renderValue: (selected: any) => {
                                                    let result = poiServices.length > 0 ? selected.map((l: any) => getStatusLabelPoi(poiServices.find((ll: any) => ll.id === l)?.value.toUpperCase() || '')).join(', ') : "";
                                                    return result.length > 100 ? result.substring(0, 100) + "..." : result;
                                                },
                                                MenuProps: MenuProps
                                            }}
                                        >
                                            {poiServices?.map((l: any) => (
                                                <MenuItem key={l.id} value={l.id}>
                                                    <Checkbox color={"accent"}
                                                              checked={field.value.indexOf(l.id) > -1}/>
                                                    <ListItemText primary={getStatusLabelPoi(l.value.toUpperCase())}/>
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    )}/>
                            </Grid>}
                        <Grid item xs={3}/>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"address"}
                                render={({field, fieldState}) => (
                                    <GoogleAutocompleteWrapper
                                        inputProps={{
                                            label: t('address'),
                                            fullWidth: true,
                                            required: true,
                                            value: field.value,
                                            onChange: field.onChange,
                                            InputProps: {
                                                endAdornment: (
                                                    <InputAdornment position={'end'}>
                                                        <IconButton onClick={cleanAddress}>
                                                            <CloseRoundedIcon/>
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            },
                                            inputProps: {
                                                form: {
                                                    autoComplete: 'off'
                                                }
                                            }
                                        }}
                                        googleInputProps={{
                                            onPlaceSelected: (place: any) => {
                                                handleCitySelection(place)
                                            },
                                            options: {
                                                fields: ['address_components', 'geometry/location'],
                                                types: ["address"],
                                            }
                                        }}
                                    />
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"city"}
                                render={({field, fieldState}) => (
                                    <TextField
                                        fullWidth
                                        name="city"
                                        required
                                        disabled={autoFilled}
                                        label={t('city')}
                                        value={field.value}
                                        onChange={field.onChange}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"latitude"}
                                render={({field, fieldState}) => (
                                    <TextField
                                        fullWidth
                                        name="latitude"
                                        required
                                        disabled={autoFilled && hasStreetNumber}
                                        label={t('latitude')}
                                        value={field.value}
                                        onChange={field.onChange}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}/>
                        </Grid>
                        <Grid item xs={3}>
                            <Controller
                                control={control}
                                name={"longitude"}
                                render={({field, fieldState}) => (
                                    <TextField
                                        fullWidth
                                        name="longitude"
                                        disabled={autoFilled && hasStreetNumber}
                                        required
                                        label={t('longitude')}
                                        value={field.value}
                                        onChange={field.onChange}
                                        error={!!fieldState.error}
                                        helperText={fieldState.error?.message}
                                    />
                                )}/>
                        </Grid>
                    </Grid>
                    <Grid container rowSpacing={3} columnSpacing={3} sx={{paddingTop: "50px"}}>
                        <Grid item>
                            <Typography>{t('typeSection.title')}</Typography>
                        </Grid>
                        <Grid item container alignItems={"center"}>
                            <Grid item xs={12}>
                                <Button
                                    sx={{float: "right"}}
                                    variant={"text"} color={"accent"}
                                    //disabled={fields.length === types.length}
                                    endIcon={<LibraryAddRounded/>} onClick={() => addSection()}>
                                    {t('typeSection.addSection')}
                                </Button>
                            </Grid>
                        </Grid>
                        <Grid item container xs={12}>
                            {fields.length === 0 ?
                                <EmptyState icon={<DoDisturbAltRounded/>}
                                            title={t('typeSection.noValuesTitle')}
                                            content={t('typeSection.noValuesContent')}/>
                                :
                                fields.map((row, index) =>
                                    <PoiType key={row.id} control={control} row={row} index={index} setValue={setValue}
                                             update={update} remove={remove} errors={formState.errors}
                                             clearErrors={clearErrors} getValues={getValues} watch={watch}/>)
                            }
                        </Grid>
                    </Grid>
                    <Grid container rowSpacing={3} columnSpacing={3} sx={{paddingTop: "50px"}}>
                        <Grid container item xs={12} rowSpacing={1} columnSpacing={3} justifyContent={"flex-end"}>
                            <Grid item>
                                <Button variant={"outlined"} color={"accent"}
                                        onClick={() => {
                                            navigate("/salesmarketing/poilist")
                                        }}>
                                    {t('buttons.cancel')}
                                </Button>
                            </Grid>
                            <Grid item>
                                <Button color={"accent"}
                                        disabled={getValues('poiTemplateDetailDTOList').length === 0 || !getValues('poiTemplateDetailDTOList')[0].type}
                                        type="submit">{t('buttons.confirm')}</Button>
                            </Grid>
                        </Grid>
                    </Grid>
                </form>
            </Paper>
            <CustomModal title={isUpdate ? t('updateForm.modalTitleSuccess') : t('createForm.modalTitleSuccess')}
                         cancelButton={t('buttons.close')}
                         open={openCreationModal}
                         titleIcon={<CheckCircleRounded color={"success"}/>}
                         onClose={() => {
                             setOpenCreationModal(false);
                             navigate("/salesmarketing/poilist")
                         }}/>
        </>
    );
}

export default PoiCreateOrUpdatePage;